uniform vec3 uColor;
uniform vec3 uColorShift;
uniform float uTime;
uniform float uOpacityVariance;
uniform float uColorVariance;
uniform float uFlashIntensity;
uniform float uDimIntensity;
uniform int uParticleShape;
uniform float uShapeSoftness;
uniform float uSaturation;
uniform float uGlobalFade;
uniform vec3 uMouseWorld;
uniform float uRevealRadius;
uniform float uRevealSoftness;
varying float vLifetime;
varying float vRandom;
varying float vRandom2;
varying float vDofBlur;
varying vec3 vPerParticleColor;
varying float vUsePerParticleColor;
varying vec3 vWorldPosition;

float hash(float n) {
    float p = fract(n * 0.1031);
    p *= p + 33.33;
    p *= p + p;
    return fract(p);
}

void main()
{
    vec2 p = gl_PointCoord - 0.5;
    float dist;

    if(uParticleShape == 0) {
        dist = length(p);
    } else if(uParticleShape == 1) {
        dist = max(abs(p.x), abs(p.y));
    } else if(uParticleShape == 2) {
        dist = abs(p.x) + abs(p.y);
    } else if(uParticleShape == 3) {
        float a = atan(p.y, p.x);
        float r = length(p);
        float star = cos(a * 4.0) * 0.3 + 0.7;
        dist = r / star;
    } else if(uParticleShape == 4) {
        float r = length(p);
        dist = abs(r - 0.3);
    } else {
        dist = length(p);
    }

    if(dist > 0.5)
        discard;

    // Soft edge — out-of-focus particles get softer edges
    float dofSoftness = uShapeSoftness + vDofBlur * 0.5;
    float edgeStart = 0.5 - dofSoftness * 0.3;
    float alpha = 1.0 - smoothstep(edgeStart, 0.5, dist);

    // DOF opacity falloff — out-of-focus particles fade
    alpha *= mix(1.0, 0.3, smoothstep(0.0, 1.0, vDofBlur));

    // Opacity variance — very slow breathing per particle
    float opacityNoise = sin(vRandom * 6.2831 + uTime * (0.04 + vRandom * 0.12)) * 0.5 + 0.5;
    alpha *= mix(1.0, opacityNoise, uOpacityVariance);

    // Fade in/out with lifetime
    float fadeIn = smoothstep(0.0, 0.1, vLifetime);
    float fadeOut = 1.0 - smoothstep(0.7, 1.0, vLifetime);
    alpha *= min(fadeIn, fadeOut);

    // Color shift over time — each particle shifts at its own phase
    float colorPhase = sin(vRandom * 6.2831 + uTime * 0.8 + vLifetime * 3.0) * 0.5 + 0.5;
    vec3 color;
    if(vUsePerParticleColor > 0.5) {
        // Per-particle color from PLY — subtle shift toward colorShift
        color = mix(vPerParticleColor, uColorShift, colorPhase * uColorVariance * 0.15);
    } else {
        color = mix(uColor, uColorShift, colorPhase * uColorVariance);
    }

    // Saturation adjustment
    float gray = dot(color, vec3(0.299, 0.587, 0.114));
    color = mix(vec3(gray), color, uSaturation);

    // 6 independent hash seeds for fully decorrelated flash/dim
    float r1 = hash(vRandom2 * 127.1 + 1.0);
    float r2 = hash(vRandom2 * 269.5 + 2.0);
    float r3 = hash(vRandom * 631.2 + vRandom2 * 3.0);
    float r4 = hash(vRandom * 971.3 + 4.0);
    float r5 = hash(vRandom2 * 853.9 + vRandom * 5.0);
    float r6 = hash(vRandom * 317.7 + vRandom2 * 6.0);

    // Flash: each particle has unique speed + phase + secondary modulator
    float flashSpeed = 0.02 + r1 * 0.15;
    float flashPhase = r2 * 6.2831 + uTime * flashSpeed;
    float flashMod = sin(r4 * 6.2831 + uTime * flashSpeed * (0.11 + r3 * 0.4));
    float flash = smoothstep(0.7, 1.0, sin(flashPhase) * flashMod);
    color += color * flash * uFlashIntensity;

    // Dim: fully independent speed + phase + secondary modulator
    float dimSpeed = 0.01 + r5 * 0.08;
    float dimPhase = r6 * 6.2831 + uTime * dimSpeed;
    float dimMod = sin(r3 * 6.2831 + uTime * dimSpeed * (0.13 + r1 * 0.5));
    float dim = smoothstep(0.7, 1.0, sin(dimPhase) * dimMod);
    alpha *= mix(1.0, 0.0, dim * min(uDimIntensity, 1.0));

    // Mouse reveal — particles only visible near the mouse
    if(uRevealRadius > 0.0) {
        float mouseDist = length(vWorldPosition.xy - uMouseWorld.xy);
        float reveal = 1.0 - smoothstep(uRevealRadius * (1.0 - uRevealSoftness), uRevealRadius, mouseDist);
        alpha *= reveal;
    }

    // Per-particle staggered fade — wide spread for visible wave effect
    float fadeThr = uGlobalFade * 2.0 - vRandom;
    alpha *= smoothstep(0.0, 0.3, fadeThr);

    gl_FragColor = vec4(color * alpha, alpha);
}
